/**
 * Client-side orchestration for the bulk-refresh feature.
 *
 * The server caps each request at 25 part_ids (BULK_REFRESH_CHUNK_LIMIT
 * in `backend/app/schemas/sourcing_bulk.py`) so the client chunks the
 * user's full selection and dispatches sequentially. This keeps live
 * progress visible in the modal without needing SSE / WebSockets / a
 * job table — for ~hundreds of parts at hobbyist scale the cost is
 * a handful of HTTP round-trips spread over the actual adapter time.
 *
 * The hook returns a state object the modal renders directly. State
 * updates are batched per chunk so React only re-renders at chunk
 * boundaries, not per-part.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { api } from './api';

const CHUNK_SIZE = 25;

export type BulkStatus = 'updated' | 'needs_review' | 'skipped' | 'error';

export interface FieldConflict {
  field: string;
  current_value: unknown;
  vendor_value: unknown;
}

export interface BulkRefreshResult {
  part_id: number;
  /** User-visible part number ("AR10100"). Falls back to null only
   * when the part wasn't found at all (skipped: part_not_found). */
  part_number: string | null;
  status: BulkStatus;
  distributor_slug: string | null;
  distributor_name: string | null;
  applied_fields: string[];
  conflicts: FieldConflict[];
  skip_reason: string | null;
  error_message: string | null;
}

interface BulkRefreshResponse {
  results: BulkRefreshResult[];
}

export interface BulkRefreshState {
  total: number;
  done: number;
  results: BulkRefreshResult[];
  /** True after the user clicked Cancel. The current chunk finishes
   * (we don't abort an in-flight HTTP request) but no new chunks
   * dispatch. */
  cancelled: boolean;
  isRunning: boolean;
  error: Error | null;
}

const INITIAL: BulkRefreshState = {
  total: 0,
  done: 0,
  results: [],
  cancelled: false,
  isRunning: false,
  error: null,
};

export function useBulkRefresh(orgSlug: string | undefined) {
  const qc = useQueryClient();
  const [state, setState] = useState<BulkRefreshState>(INITIAL);
  // Mutable cancel flag — useRef so the running loop sees the latest
  // value without re-triggering on every state update.
  const cancelRef = useRef(false);

  const start = useCallback(
    async (partIds: number[]) => {
      if (!orgSlug || partIds.length === 0) return;
      cancelRef.current = false;
      setState({
        total: partIds.length,
        done: 0,
        results: [],
        cancelled: false,
        isRunning: true,
        error: null,
      });

      for (let i = 0; i < partIds.length; i += CHUNK_SIZE) {
        if (cancelRef.current) {
          setState((s) => ({ ...s, cancelled: true, isRunning: false }));
          return;
        }
        const chunk = partIds.slice(i, i + CHUNK_SIZE);
        try {
          const response = await api.postJson<BulkRefreshResponse>(
            `/api/orgs/${orgSlug}/parts/bulk-refresh`,
            { part_ids: chunk },
          );
          setState((s) => ({
            ...s,
            done: Math.min(s.total, s.done + chunk.length),
            results: [...s.results, ...response.results],
          }));
        } catch (err) {
          setState((s) => ({
            ...s,
            error: err as Error,
            isRunning: false,
          }));
          return;
        }
      }

      setState((s) => ({ ...s, isRunning: false }));
      // Refresh derived caches — parts list summary surfaces some of
      // the fields we just filled, suppliers' last_synced_at moved.
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
      qc.invalidateQueries({ queryKey: ['suppliers', orgSlug] });
    },
    [orgSlug, qc],
  );

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const reset = useCallback(() => {
    cancelRef.current = false;
    setState(INITIAL);
  }, []);

  return { state, start, cancel, reset };
}
